import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Clock, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { ProviderTabs } from "../components/ProviderTabs";
import { RecentlyPlayedGrid } from "../components/RecentlyPlayedGrid";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { Track } from "../types";

export const RecentlyPlayed = () => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const { isAuthenticated, login } = useSpotifyAuth();
  const { getRecentlyPlayed, initialized } = useLocalLibraryStore();
  const { setCurrentTrack, setIsPlaying } = usePlayerStore();
  const [isLoading, setIsLoading] = useState(!initialized);

  useEffect(() => {
    if (!initialized) {
      const checkInitialization = setInterval(() => {
        if (initialized) {
          setIsLoading(false);
          clearInterval(checkInitialization);
        }
      }, 100);

      return () => clearInterval(checkInitialization);
    } else {
      setIsLoading(false);
    }
  }, [initialized]);

  const { data: recentTracks, isLoading: isSpotifyLoading } = useQuery({
    queryKey: ["recent-tracks"],
    queryFn: () => spotifyApi.player.getRecentlyPlayedTracks(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const localTracks = getRecentlyPlayed();

  const handlePlay = (track: Track) => {
    setCurrentTrack(track);
    setIsPlaying(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-yellow-500" />
          <p className="text-zinc-400">Loading recently played tracks...</p>
        </motion.div>
      </div>
    );
  }

  const renderContent = () => {
    if (activeProvider === "spotify") {
      if (!isAuthenticated) {
        return (
          <EmptyState
            icon={Clock}
            title="Connect to Spotify"
            description="Connect your Spotify account to view your recently played tracks"
            action={{
              label: "Connect Spotify",
              onClick: login,
            }}
          />
        );
      }

      if (isSpotifyLoading) {
        return (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          </div>
        );
      }

      if (!recentTracks?.items.length) {
        return (
          <EmptyState
            icon={Clock}
            title="No Recent Tracks"
            description="Start listening to some music on Spotify"
            action={{
              label: "Browse Music",
              href: "/search",
            }}
          />
        );
      }

      return (
        <RecentlyPlayedGrid
          tracks={recentTracks.items.map((item) => ({
            id: item.track.id,
            title: item.track.name,
            artist: item.track.artists[0].name,
            albumArt: item.track.album.images[0]?.url,
            duration: item.track.duration_ms / 1000,
            url: item.track.preview_url || "",
            source: "spotify" as const,
            spotifyData: item.track,
            album: item.track.album.name,
          }))}
          onPlay={handlePlay}
        />
      );
    }

    if (!localTracks.length) {
      return (
        <EmptyState
          icon={Clock}
          title="No Recent Tracks"
          description="Start listening to your local music"
          action={{
            label: "Browse Files",
            href: "/local",
          }}
        />
      );
    }

    return (
      <RecentlyPlayedGrid
        tracks={localTracks.map((track) => ({
          ...track,
          album: track.album || "Unknown Album",
          artist: track.artist || "Unknown Artist",
          duration: track.duration || 0,
          url: track.url || "",
        }))}
        onPlay={handlePlay}
      />
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-4xl font-bold mb-2">Recently Played</h1>
        </div>
      </div>
      <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

      {renderContent()}
    </div>
  );
};
